import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal } from 'antd';
import { ForgotPasswordMethod } from 'src/shared/interface/forgot-password';
import { VerifyStepContainer } from './containers/VerifyStepContainer';
import { OTPStepContainer } from './containers/OTPStepContainer';
import { ResetPasswordStepContainer } from './containers/ResetPasswordStepContainer';
import { SuccessScreen } from './components/SuccessScreen';
import { useAppDispatch, useAppSelector } from 'src/redux/hook';
import {
  setMethod,
  verifySuccess,
  otpSuccess,
  resetPasswordSuccess,
  goBack,
  updateOTP,
  reset,
} from 'src/redux/slice/forgotPasswordSlice';
import { APP_CODE, FORGOT_PASSWORD_STEP, SESSION_TIMEOUT } from 'src/shared/constants';
import '../login/index.scss';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.forgotPassword);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    const urlStep = searchParams.get('step');
    const isRefreshOnResetPassword =
      urlStep === 'new-password' && state.currentStep === FORGOT_PASSWORD_STEP.NEW_PASSWORD;

    if (isRefreshOnResetPassword) {
      dispatch(reset());
      navigate('/forgot-password', { replace: true });
      return;
    }

    // Check session timeout
    if (state.lastUpdated && Date.now() - state.lastUpdated > SESSION_TIMEOUT) {
      dispatch(reset());
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { currentStep, method, username, email, phoneNumber } = state;
    const params = new URLSearchParams();

    const shouldClearParams =
      currentStep === FORGOT_PASSWORD_STEP.METHOD_SELECTION ||
      currentStep === FORGOT_PASSWORD_STEP.SUCCESS;

    if (shouldClearParams) {
      setSearchParams({}, { replace: true });
      return;
    }

    // Set common params
    if (method) params.set('method', String(method));
    if (username) params.set('username', username);
    params.set('appCode', APP_CODE);

    if (currentStep === FORGOT_PASSWORD_STEP.OTP_INPUT) {
      params.set('step', 'otp');
      const identifier = method === ForgotPasswordMethod.EMAIL ? email : phoneNumber;
      if (identifier) params.set('identifier', identifier);
    } else if (currentStep === FORGOT_PASSWORD_STEP.NEW_PASSWORD) {
      params.set('step', 'new-password');
    }

    setSearchParams(params, { replace: true });
  }, [state, setSearchParams]);

  const handleBack = useCallback(() => {
    if (state.currentStep === FORGOT_PASSWORD_STEP.NEW_PASSWORD) {
      Modal.confirm({
        title: 'Xác nhận',
        content: 'Bạn có chắc muốn quay lại? Phiên làm việc sẽ bị reset.',
        okText: 'Đồng ý',
        cancelText: 'Hủy',
        onOk: () => {
          dispatch(reset());
        },
      });
      return;
    }

    if (
      state.currentStep === FORGOT_PASSWORD_STEP.METHOD_SELECTION ||
      state.currentStep === FORGOT_PASSWORD_STEP.VERIFY_INFO
    ) {
      navigate('/login');
    } else {
      dispatch(goBack());
    }
  }, [state.currentStep, navigate, dispatch]);

  const handleGoToLogin = useCallback(() => {
    dispatch(reset());
    navigate('/login');
  }, [navigate, dispatch]);

  const renderContent = () => {
    const { currentStep, method, username, email, phoneNumber, otp } = state;

    switch (currentStep) {
      case FORGOT_PASSWORD_STEP.METHOD_SELECTION:
      case FORGOT_PASSWORD_STEP.VERIFY_INFO:
        return (
          <VerifyStepContainer
            method={method || ForgotPasswordMethod.EMAIL}
            onMethodChange={(method) => dispatch(setMethod(method))}
            onSuccess={(username, identifier, otpData) =>
              dispatch(verifySuccess({ username, identifier, otpData }))
            }
            onBack={handleBack}
          />
        );

      case FORGOT_PASSWORD_STEP.OTP_INPUT:
        return (
          <OTPStepContainer
            method={method || ForgotPasswordMethod.EMAIL}
            username={username}
            identifier={method === ForgotPasswordMethod.EMAIL ? email : phoneNumber}
            otpState={otp}
            onSuccess={(otp, token) => dispatch(otpSuccess({ otp, token }))}
            onOTPStateChange={(otpState) => dispatch(updateOTP(otpState))}
            onBack={handleBack}
          />
        );

      case FORGOT_PASSWORD_STEP.NEW_PASSWORD:
        return (
          <ResetPasswordStepContainer
            username={username}
            otpToken={otp.token}
            onSuccess={() => dispatch(resetPasswordSuccess())}
            onBack={handleBack}
            onSessionExpired={() => {
              dispatch(reset());
              dispatch(setMethod(ForgotPasswordMethod.EMAIL));
            }}
          />
        );

      case FORGOT_PASSWORD_STEP.SUCCESS:
        return <SuccessScreen onGoToLogin={handleGoToLogin} />;

      default:
        return null;
    }
  };

  return (
    <div className='w-screen h-screen wrapper'>
      <div className='p-6 login-form max-w-md'>
        <div className='mb-6 text-center'>
          <h4 className='text-center text-2xl font-bold mb-2'>Đặt lại mật khẩu</h4>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default ForgotPassword;
